import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { electricProviderInfoService } from '../../../../services/electricProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('ElectricProviderData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const ElectricProviderDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();
  const [electricProviderData, setElectricProviderData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadElectricProviderData();
  }, []);

  const loadElectricProviderData = async () => {
    try {
      setLoading(true);
      const data = await electricProviderInfoService.getAllElectricProviderInfo(targetUserId);
      
      if (data && data.length > 0) {
        // Load portal credentials for each electric provider
        const updatedData = await Promise.all(
          data.map(async (electricProvider) => {
            // Skip loading portal credentials in viewOnly mode - they are protected until EOL
            if (!viewOnly || isDeathVerifiedForSelectedUser) {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for electric provider ID:', electricProvider.id);
                const portalData = await vaultService.getPortalCredentials(electricProvider.id, 'electric-provider');
                logger.debug('[Portal Debug] Portal data retrieved for electric provider:', electricProvider.id, portalData);
                if (portalData) {
                  electricProvider.username = portalData.username;
                  electricProvider.password = portalData.password;
                  electricProvider.accountNumber = portalData.accountNumber;
                  logger.debug('[Portal Debug] Assigned credentials to electric provider data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for electric provider:', electricProvider.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for electric provider:', electricProvider.id, vaultError);
              }
            }
            return electricProvider;
          })
        );

        logger.debug('[Data Debug] Final electric provider data being set:', updatedData.length, 'providers');
        logger.debug('[Data Debug] First provider account number:', updatedData[0]?.accountNumber);
        setElectricProviderData(updatedData);
      }
    } catch (error) {
      logger.error('Error loading electric provider data:', error);
      Alert.alert('Error', 'Failed to load electric provider information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const handleEditElectricProvider = (electricProvider: any) => {
    navigation.navigate('ElectricProviderInput', { 
      electricProviderId: electricProvider.id, 
      isEdit: true 
    });
  };

  const handleDeleteElectricProvider = async (electricProvider: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this electric provider information?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await electricProviderInfoService.deleteElectricProviderInfo(electricProvider.id);
            
            // Reload data after deletion
            await loadElectricProviderData();
            
            Alert.alert('Deleted', 'Electric provider information deleted.');
          } catch (e) {
            logger.error('Delete electric provider info failed:', e);
            Alert.alert('Error', 'Failed to delete electric provider information');
          }
        } 
      },
    ]);
  };

  const handleAddElectricProvider = () => {
    navigation.navigate('ElectricProviderInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Electric Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading electric provider information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!electricProviderData || electricProviderData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Electric Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No electric provider information found</Text>
            <TouchableOpacity
              style={styles.addButton}
              onPress={handleAddElectricProvider}
            >
              <Text style={styles.addButtonText}>Add Electric Provider Info</Text>
            </TouchableOpacity>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Electric Provider Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {electricProviderData.map((electricProvider, index) => (
            <View key={electricProvider.id || index} style={styles.electricProviderCard}>
              <View style={styles.electricProviderHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.electricProviderName}>
                    {electricProvider.companyName || 'Unknown Provider'}
                  </Text>
                  <Text style={styles.electricProviderType}>
                    {electricProvider.ratePlan || 'Not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editElectricProviderButton}
                      onPress={() => handleEditElectricProvider(electricProvider)}
                    >
                      <Text style={styles.editElectricProviderButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteElectricProviderButton}
                      onPress={() => handleDeleteElectricProvider(electricProvider)}
                    >
                      <Text style={styles.deleteElectricProviderButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Company Name" value={electricProvider.companyName || ''} />
              
              {electricProvider.accountNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Account Number 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showAccountNumber[electricProvider.id] ? electricProvider.accountNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowAccountNumber(prev => ({ ...prev, [electricProvider.id]: !prev[electricProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showAccountNumber[electricProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              <InfoItem label="Service Address" value={electricProvider.serviceAddress || ''} />
              <InfoItem label="Billing Address" value={electricProvider.billingAddress || ''} />
              <InfoItem label="Customer Service Phone" value={formatPhoneNumber(electricProvider.customerServicePhone) || ''} />
              <InfoItem label="Emergency Phone" value={formatPhoneNumber(electricProvider.emergencyPhone) || ''} />
              <InfoItem label="Website" value={electricProvider.website || ''} />
              <InfoItem label="Meter Number" value={electricProvider.meterNumber || ''} />
              <InfoItem label="Rate Plan" value={electricProvider.ratePlan || ''} />
              <InfoItem label="Average Monthly Cost" value={formatCurrency(electricProvider.averageMonthlyCost) || ''} />
              <InfoItem label="Autopay Enabled" value={electricProvider.autopayEnabled ? 'Yes' : 'No'} />
              <InfoItem label="Autopay Method" value={electricProvider.autopayMethod || ''} />
              <InfoItem label="Contract End Date" value={electricProvider.contractEndDate || ''} />
              
              {electricProvider.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[electricProvider.id] ? electricProvider.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [electricProvider.id]: !prev[electricProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[electricProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {electricProvider.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[electricProvider.id] ? electricProvider.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [electricProvider.id]: !prev[electricProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[electricProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={electricProvider.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherElectricProviderButton}
                onPress={handleAddElectricProvider}
              >
                <Text style={styles.addAnotherElectricProviderButtonText}>Add Another Electric Provider</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  electricProviderCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  electricProviderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  electricProviderName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  electricProviderType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editElectricProviderButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editElectricProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteElectricProviderButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteElectricProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherElectricProviderButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherElectricProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
